import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PickSessionOrdersModal } from "../../wms/pick-sessions/pick-session-orders-modal";
import { verifyForm } from "../common";
import { PickSessionContext } from "../contexts/pick-session-context";

interface ToteOrderSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
  pending: boolean;
}

export const ToteOrderSelectionModal = memo(function ToteOrderSelectionModal({
  isOpen,
  onClose,
  onScan,
  pending,
}: ToteOrderSelectionModalProps) {
  const form = useInput(verifyForm, { barcode: "" });
  const { barcode } = form.inputs;
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const params = useParams<{ fulfillmentGroupId?: string }>();
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const pickSessionContext = useRequiredContext(PickSessionContext);

  const handleNavigate = (value: string) => {
    navigate(
      `/stores/${team._id}/order-management/outbound-labels/scan/${value}`,
    );
    onClose();
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Small delay to ensure modal is fully rendered
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen]);

  return (
    <RedoModal
      isOpen={isOpen}
      onModalCloseRequested={() => {
        barcode.setValue("");
        onClose();
      }}
      title="Scan any item from this tote"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onScan(barcode.value);
        }}
        style={{ display: "flex" }}
      >
        <Flex align="center" gap="lg">
          <Flex gap="xl">
            <Flex
              basis={0}
              data-testid="scan-page-lookup-fulfillment-id-input"
              flex={1}
            >
              <RedoTextInput
                placeholder="Enter item barcode"
                ref={inputRef}
                setValue={barcode.setValue}
                size="sm"
                value={barcode.value}
              />
            </Flex>
          </Flex>
          <Flex>
            <RedoButton
              disabled={!!form.allErrors.length}
              hierarchy="primary"
              pending={pending}
              text="Find shipment"
              theme="success"
              type="submit"
            />
          </Flex>
          {pickSessionContext?.pickSession && (
            <Flex>
              <RedoButton
                hierarchy="tertiary"
                onClick={() => setOrdersModalOpen(true)}
                text="View orders"
              />
            </Flex>
          )}
        </Flex>
      </form>

      {pickSessionContext?.pickSession && (
        <PickSessionOrdersModal
          fulfillmentGroupIdInView={params.fulfillmentGroupId}
          handleNavigate={handleNavigate}
          isOpen={ordersModalOpen}
          onClose={() => setOrdersModalOpen(false)}
          onPickSessionUpdate={pickSessionContext.setPickSession}
          pickSession={pickSessionContext.pickSession}
        />
      )}
    </RedoModal>
  );
});
